import { Loader2 } from "lucide-react"
import { useEffect, useRef } from "react"
import { PostCard } from "@/entities/post"
import type { Post } from "@/shared/lib/types"
import { RevealItem, RevealList } from "@/shared/ui/reveal"
import { PostCardActions } from "./post-card-actions"

const STAGGERED_COUNT = 6

interface PostGridProps {
	posts: Post[]
	hasNextPage?: boolean
	isFetchingNextPage?: boolean
	onLoadMore?: () => void
}

export function PostGrid({ posts, hasNextPage, isFetchingNextPage, onLoadMore }: PostGridProps) {
	const observerRef = useRef<HTMLDivElement>(null)

	useEffect(() => {
		if (!onLoadMore || !hasNextPage || isFetchingNextPage) return

		const observer = new IntersectionObserver(
			(entries) => {
				if (entries[0].isIntersecting) {
					onLoadMore()
				}
			},
			{ threshold: 0.1 },
		)

		if (observerRef.current) {
			observer.observe(observerRef.current)
		}

		return () => observer.disconnect()
	}, [onLoadMore, hasNextPage, isFetchingNextPage])

	return (
		<>
			<RevealList className="grid grid-cols-1 gap-4 lg:gap-6">
				{posts.map((post, index) =>
					index < STAGGERED_COUNT ? (
						<RevealItem key={post.id}>
							<PostCard post={post} actions={<PostCardActions postId={post.id} />} />
						</RevealItem>
					) : (
						<PostCard key={post.id} post={post} actions={<PostCardActions postId={post.id} />} />
					),
				)}
			</RevealList>

			{onLoadMore && hasNextPage && (
				<div ref={observerRef} className="py-8">
					{isFetchingNextPage && (
						<div className="flex justify-center">
							<Loader2 className="h-8 w-8 animate-spin text-primary" />
						</div>
					)}
				</div>
			)}
		</>
	)
}
